import { useState } from 'react';
import { Search } from 'lucide-react';
import useCatalog from '../hooks/useCatalog';
import { CATS, DN_PIPES, D_GARDEN, gardenItems, matchItems, inSector, bySector, MEDS, MED_COLOR, medsOf } from '../lib/catalog';
import { getMode, isSewer, modeId } from '../lib/profiles';
import { eur } from '../lib/license';
// in-house: plan / sketch shown on the map -> building pipes, aerial view -> service and garden line only
const onPlanView = () => !!document.querySelector('.leaflet-container.floor-mode');

export function DnSelect({ t, value, onChange, testid }) {
  const gd = getMode() === 'garden', list = gd ? D_GARDEN : DN_PIPES, v = +(String(value ?? '').match(/\d+(\.\d+)?/)?.[0] || 0) || '';
  return (
    <select data-testid={testid} value={v} onChange={(e) => onChange(e.target.value ? +e.target.value : '')}>
      <option value="">{t.catPickDn}</option>
      {v && !list.includes(v) && <option value={v}>{gd ? `d${v}` : `DN ${v}`}</option>}
      {list.map((d) => <option key={d} value={d}>{gd ? `d${d}` : `DN ${d}`}</option>)}
    </select>
  );
}

export function CatalogSelect({ t, kind, value, onChange, testid }) {
  const { items, sector } = useCatalog();
  const mine = items.filter((x) => (x.kind === 'pipe') === (kind === 'pipe'));
  const own = getMode() === 'garden' ? gardenItems(mine) : sector && sector !== 'all' ? mine.filter((x) => inSector(x, sector) || x.id === value) : bySector(mine, sector);
  const cats = [...new Set(own.map((x) => x.cat))].filter((c) => CATS.includes(c));
  return (
    <select data-testid={testid} value={value || ''} onChange={(e) => onChange(own.find((x) => x.id === e.target.value))}>
      {cats.map((c) => {
        const list = own.filter((x) => x.cat === c);
        return list.length ? <optgroup key={c} label={t['cat_' + c]}>{list.map((x) => <option key={x.id} value={x.id}>{`${x.name}${x.dim ? ' · ' + x.dim : ''}`}</option>)}</optgroup> : null;
      })}
    </select>
  );
}

export default function CatalogPicker({ t, kind, onPick, testid }) {
  const { items, sector } = useCatalog();
  const [allSec, setAllSec] = useState(false);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [dn, setDnS] = useState(() => (getMode() === 'garden' ? localStorage.getItem('giss_gd_dn') || '' : isSewer() ? 200 : 100));
  const setDn = (v) => { if (getMode() === 'garden') localStorage.setItem('giss_gd_dn', v); setDnS(v); };
  const [med, setMedS] = useState(() => localStorage.getItem('giss_med') || '');
  const setMed = (m) => { localStorage.setItem('giss_med', m); setMedS(m); setCat(''); };
  const kindItems = items.filter((x) => (x.kind === 'pipe') === (kind === 'pipe'));
  const showMed = kindItems.some((x) => medsOf(x) && inSector(x, sector));
  const medList = MEDS.filter((m) => kindItems.some((x) => (!sector || sector === 'all' || inSector(x, sector)) && medsOf(x)?.includes(m)));
  const useMed = showMed && medList.includes(med) ? med : '';
  // the item's own medium wins when the active chip does not belong to it
  const medFor = (x) => (useMed && medsOf(x)?.includes(useMed) ? useMed : medsOf(x)?.[0] || useMed || null);
  const spec = (x) => (/^[frt]\d/.test(x.id || '') ? 0 : 1);
  const ihp = kind === 'pipe' && modeId() === 'detect_inhouse' && !allSec ? (onPlanView() ? (x) => /^i(1[4-9]|20)$/.test(x.id) : (x) => /^i2[12]$/.test(x.id)) : null;
  const base = kindItems.filter((x) => allSec || inSector(x, sector) || (useMed && medsOf(x)?.includes(useMed))).filter((x) => !ihp || x.sector !== 'inhouse' || ihp(x));
  // a chosen medium only sorts its parts to the top - nothing is hidden
  const irr = (x) => (/^g(0[1-5]|19|[23]\d|4[0-2])$/.test(x.id || '') ? 0 : 1);
  const fw = (x) => (x.type === 'heat' || medsOf(x)?.includes('heat') ? 0 : 1);
  const own = useMed ? base.filter((x) => medsOf(x)?.includes(useMed)).sort((a, b) => spec(a) - spec(b)) : sector === 'garden' ? [...base].sort((a, b) => irr(a) - irr(b)) : modeId() === 'detect_heat' ? [...base].sort((a, b) => fw(a) - fw(b)) : base;
  const rest = useMed ? base.filter((x) => !medsOf(x)?.includes(useMed)) : [];
  const cats = CATS.filter((c) => [...own, ...rest].some((x) => x.cat === c));
  const list = matchItems(own, q, cat), more = matchItems(rest, q, cat);
  return (
    <div className="cat-picker" data-testid={testid}>
      <label className="cat-search"><Search size={14} /><input data-testid={`${testid}-search`} value={q} placeholder={t.catSearch} onChange={(e) => setQ(e.target.value)} /></label>
      {kind === 'pipe' && !['survey', 'detect_cable'].includes(modeId()) && <DnSelect t={t} value={dn} onChange={setDn} testid={`${testid}-dn`} />}
      {showMed && (
        <div className="cat-chips med-chips" data-testid={`${testid}-med`}>
          <b className="med-lbl">{t.medTitle}:</b>
          <button className={`chip seg ${!useMed ? 'on' : ''}`} data-testid={`${testid}-med-all`} onClick={() => setMed('')}>{t.medAll}</button>
          {medList.map((m) => <button key={m} className={`chip seg med-chip ${useMed === m ? 'on' : ''}`} style={{ '--med': MED_COLOR[m] }} data-testid={`${testid}-med-${m}`} onClick={() => setMed(m)}>{t['med_' + m]}</button>)}
        </div>
      )}
      <div className="cat-chips">
        {sector && sector !== 'all' && <button className={`chip seg ${allSec ? 'on' : ''}`} data-testid={`${testid}-all-sectors`} onClick={() => setAllSec((v) => !v)}>{t.secAllShort}</button>}
        <button className={`chip seg ${!cat ? 'on' : ''}`} data-testid={`${testid}-cat-all`} onClick={() => setCat('')}>{t.catAll}</button>
        {cats.map((c) => <button key={c} className={`chip seg ${cat === c ? 'on' : ''}`} data-testid={`${testid}-cat-${c}`} onClick={() => setCat(c)}>{t['cat_' + c]}</button>)}
      </div>
      <div className="cat-list">
        {list.map((x) => (
          <button key={x.id} className="fs-item cat-item" data-testid={`${testid}-item-${x.id}`} onClick={() => onPick({ ...x, med: medFor(x) }, kind === 'pipe' ? dn : '')}>
            <i className="fs-dot" style={{ background: x.color }}>{x.sym}</i>
            <span>{x.name}<small>{[kind === 'pipe' && dn && x.sector === 'garden' ? String(x.dim || '').replace(/d\d+/, `d${dn}`) : x.dim, x.price != null ? eur(x.price, t.locale) : ''].filter(Boolean).join(' · ')}</small></span>
          </button>
        ))}
        {more.length > 0 && <div className="cat-more" data-testid={`${testid}-more`}>{t.medMore}</div>}
        {more.map((x) => (
          <button key={x.id} className="fs-item cat-item" data-testid={`${testid}-item-${x.id}`} onClick={() => onPick({ ...x, med: medFor(x) }, kind === 'pipe' ? dn : '')}>
            <i className="fs-dot" style={{ background: x.color }}>{x.sym}</i>
            <span>{x.name}<small>{[kind === 'pipe' && dn && x.sector === 'garden' ? String(x.dim || '').replace(/d\d+/, `d${dn}`) : x.dim, x.price != null ? eur(x.price, t.locale) : ''].filter(Boolean).join(' · ')}</small></span>
          </button>
        ))}
      </div>
    </div>
  );
}
